<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { _ } from '../lib/i18n.js';
  import { customersApi, productsApi, timesheetsApi } from '../stores/api.js';
  import { X, Edit2, Clock, Info } from 'lucide-svelte';

  export let timesheet = null;

  const dispatch = createEventDispatcher();
  const isEdit = !!timesheet;

  // Activity types enum
  const ACTIVITY_TYPES = ['development', 'planning', 'admin', 'sales', 'communication', 'consulting'];

  let formData = {
    date: new Date().toISOString().split('T')[0],
    hours: 8,
    description: '',
    customerId: '',
    billable: true,
    hourlyRate: null,
    activityType: '',
    variantId: null,
    urgent: false,
    night: false
  };

  let errors = {};
  let error = ''; // Server-side error surfaced from the save call, shown inside the modal
  let loading = false;
  let customers = [];
  let loadingCustomers = false;
  let lookingUpRate = false;
  let rateSource = null; // 'customer' or 'variant' or null
  // Seeded from the entry itself, not left empty: the badge explains the rate
  // shown in the field, and for a saved entry that rate is the stored one, so
  // the stored applied_condition is the only honest explanation for it until a
  // lookup actually re-prices the entry.
  let appliedCondition = timesheet?.appliedCondition ?? null; // 'weekend' | 'holiday' | 'urgent' | 'night' | null
  let noRateConfigured = false; // variant is linked but has no price at all (source === null)
  let notHourly = false; // variant is linked but not billed by the hour (hourly === false)
  let linkedVariantInfo = null; // Info about linked variant when found
  let lastLookupKey = ''; // Prevent duplicate lookups

  // Initialize form data
  if (timesheet) {
    formData = {
      date: timesheet.date ? new Date(timesheet.date).toISOString().split('T')[0] : formData.date,
      hours: timesheet.hours || 8,
      description: timesheet.description || '',
      customerId: timesheet.customerId || '',
      billable: timesheet.billable !== undefined ? timesheet.billable : true,
      // Nullish, not ||: a genuine stored rate of 0.00 must survive, not
      // collapse into "no rate" just because 0 is falsy.
      hourlyRate: timesheet.hourlyRate ?? null,
      activityType: timesheet.activityType || '',
      variantId: timesheet.variantId || null,
      urgent: timesheet.urgent || false,
      night: timesheet.night || false
    };
  }

  // The reactive block below fires a lookup the moment an existing entry
  // that already has an activity type is opened (lastLookupKey starts
  // empty). That automatic lookup must not overwrite a rate the entry was
  // actually saved with — the price list may have moved since, and the
  // server rejects a rate change on fields the user never touched. This is
  // tied to "is there a rate worth protecting", not to "is this the first
  // lookup call": an entry that opens with NO activity type never fires that
  // automatic lookup at all, so when the user then picks one for the first
  // time, that lookup is genuinely user-triggered and must price normally —
  // there is nothing saved under an activity type to protect. Consumed
  // synchronously (not in the lookup's `finally`) so a second, genuine
  // lookup that happens to resolve before this one can never race it: at
  // most one in-flight call can ever end up protecting the stored rate.
  let hasUnverifiedStoredRate = isEdit && !!formData.activityType && formData.hourlyRate !== null;

  onMount(async () => {
    await loadCustomers();
  });

  async function loadCustomers() {
    loadingCustomers = true;
    try {
      console.log('[TimesheetModal] Loading customers...');
      const response = await customersApi.getAll({ limit: 1000 });
      customers = response.customers || [];
      console.log(`[TimesheetModal] Loaded ${customers.length} customers`);
    } catch (err) {
      console.error('[TimesheetModal] Error loading customers:', err);
    } finally {
      loadingCustomers = false;
    }
  }

  /**
   * Do these two rates mean the same money? Both have to be present: a null
   * or empty on either side means "no rate", and Number(null) === 0 would
   * otherwise call it a match with a genuine, deliberate 0.00.
   *
   * @param {number|string|null|undefined} a
   * @param {number|string|null|undefined} b
   * @returns {boolean}
   */
  function sameRate(a, b) {
    const present = (value) => value !== null && value !== undefined && value !== '';
    if (!present(a) || !present(b)) return false;
    return Number(a) === Number(b);
  }

  async function lookupRate() {
    if (!formData.activityType) {
      linkedVariantInfo = null;
      rateSource = null;
      appliedCondition = null;
      noRateConfigured = false;
      notHourly = false;
      return;
    }

    // Prevent duplicate lookups with same parameters. Date and the two
    // surcharge flags are part of the key so moving the date or toggling
    // urgent/night re-prices instead of reusing a stale result.
    const lookupKey = `${formData.activityType}|${formData.customerId || ''}|${formData.date}|${formData.urgent}|${formData.night}`;
    if (lookupKey === lastLookupKey) {
      return;
    }
    lastLookupKey = lookupKey;

    // Capture-and-clear synchronously, before the `await` below: whichever
    // call gets here first (in dispatch order, not resolution order) is the
    // only one that can ever protect the stored rate.
    const protectStoredRate = hasUnverifiedStoredRate;
    hasUnverifiedStoredRate = false;

    lookingUpRate = true;
    try {
      console.log('[TimesheetModal] Looking up rate by activity type...', {
        activityType: formData.activityType,
        customerId: formData.customerId || 'none',
        date: formData.date,
        urgent: formData.urgent,
        night: formData.night
      });

      const result = await productsApi.rateLookup(formData.activityType, formData.customerId || null, {
        date: formData.date,
        urgent: formData.urgent,
        night: formData.night
      });

      if (result.found) {
        // Gated on the same flag as the rate below. The activity-type link can
        // resolve to a different variant than the one the entry was saved
        // against (the link was re-pointed, a new variant took over that
        // activity type), and this modal has no variant selector: assigning it
        // unconditionally would submit a variant the user never saw, on which
        // the server then re-prices the entry to a number they were never
        // shown - or refuses the save outright. A lookup that is protecting a
        // stored rate must leave the entry pointing where it already points.
        if (!protectStoredRate) {
          formData.variantId = result.variantId;
        }
        linkedVariantInfo = {
          code: result.variantCode,
          name: result.variantName
        };
        // The source pill and the condition badge explain the number in the
        // rate field, so they may only be taken from this lookup when this
        // lookup actually produced that number. A protected stored rate keeps
        // the condition the entry was saved with - unless the fresh
        // resolution lands on the very same price, in which case the fresh
        // explanation does describe what is on screen.
        const rateExplained = !protectStoredRate || sameRate(formData.hourlyRate, result.hourlyRate);
        notHourly = result.hourly === false;

        if (notHourly) {
          // Non-hourly: the server honours whatever rate the client sends
          // for these variants (the price list's unit price is not an
          // hourly rate and is never auto-filled) — the rate is the user's
          // to type, so a stored or already-typed value here is never
          // erased. Only badge/source tracking is reset.
          rateSource = null;
          noRateConfigured = false;
        } else if (result.source === null) {
          // Variant is linked but has no price configured at all: hourlyRate
          // comes back as 0, which would look like a real free rate if shown
          // as-is. Surface the gap instead of a misleading €0,00 — but only
          // when there's nothing stored worth protecting (see
          // hasUnverifiedStoredRate above), or an entry saved while a price
          // existed would lose it the moment it's reopened after that price
          // is removed from the list.
          rateSource = result.source;
          noRateConfigured = true;
          if (!protectStoredRate) {
            formData.hourlyRate = null;
          }
        } else {
          rateSource = result.source;
          noRateConfigured = false;
          if (!protectStoredRate) {
            formData.hourlyRate = result.hourlyRate;
          }
        }

        if (rateExplained) {
          // A non-hourly variant is never surcharge-priced, so it has no
          // condition to name - the server stores null for it too.
          appliedCondition = notHourly ? null : result.appliedCondition;
        } else {
          // The field is showing the stored rate and this lookup resolved a
          // different one: naming this lookup's rule next to that number
          // would say the entry was priced by a rule it was not. Keep the
          // condition the entry itself was saved with, and drop the source
          // pill, which the entry does not record.
          rateSource = null;
        }
        console.log(`[TimesheetModal] Found rate: €${result.hourlyRate} from ${result.variantCode} (source: ${result.source}, condition: ${result.appliedCondition}, hourly: ${result.hourly})`);
      } else {
        // Same gate, same reason as the assignment above: "the activity type
        // no longer resolves to a variant" is not "the user unlinked this
        // entry's variant". Clearing it here would silently submit
        // variantId: null for an entry the user only opened, dropping the
        // link (and the applied condition with it) with nothing on screen to
        // show for it. A later, user-triggered lookup is unprotected and does
        // clear it, which is the point at which they are actually editing.
        if (!protectStoredRate) {
          formData.variantId = null;
        }
        linkedVariantInfo = null;
        rateSource = null;
        appliedCondition = null;
        noRateConfigured = false;
        notHourly = false;
        console.log('[TimesheetModal] No variant linked to this activity type');
      }
    } catch (err) {
      console.error('[TimesheetModal] Error looking up rate:', err);
      linkedVariantInfo = null;
      rateSource = null;
      appliedCondition = null;
      noRateConfigured = false;
      notHourly = false;
    } finally {
      lookingUpRate = false;
    }
  }

  // Watch for changes in activity type, customer, date or the surcharge
  // flags to trigger a rate lookup.
  $: if (formData.activityType || formData.customerId !== undefined) {
    // Reset lookup key when customer changes to allow new lookup
    if (formData.activityType) {
      const newKey = `${formData.activityType}|${formData.customerId || ''}|${formData.date}|${formData.urgent}|${formData.night}`;
      if (newKey !== lastLookupKey) {
        lookupRate();
      }
    }
  }

  function formatCurrency(amount) {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }

  // A presence check, not a truthiness check: a genuine rate of 0.00 is a
  // real value, not "empty". Shared by the submit payload and the
  // calculated-total preview so neither treats a free/comped rate as unset.
  $: hasHourlyRate = formData.hourlyRate !== null && formData.hourlyRate !== undefined && formData.hourlyRate !== '';

  async function handleSubmit() {
    errors = {};
    error = '';

    // Validation
    if (!formData.date) {
      errors.date = $_('timesheets.validation.dateRequired');
    }

    const hours = parseFloat(formData.hours);
    if (isNaN(hours) || hours <= 0) {
      errors.hours = $_('timesheets.validation.hoursRequired');
    } else if (hours < 0.25) {
      errors.hours = $_('timesheets.validation.hoursMin');
    } else if (hours > 24) {
      errors.hours = $_('timesheets.validation.hoursMax');
    }

    if (Object.keys(errors).length > 0) {
      return;
    }

    // Prepare data. hourlyRate is still sent for the manual, variant-less
    // path; when a variant is linked the server resolves the rate itself
    // and ignores this value.
    const data = {
      date: formData.date,
      hours: parseFloat(formData.hours),
      description: formData.description?.trim() || null,
      customerId: formData.customerId || null,
      billable: formData.billable,
      hourlyRate: hasHourlyRate ? parseFloat(formData.hourlyRate) : null,
      activityType: formData.activityType || null,
      variantId: formData.variantId || null,
      urgent: formData.urgent,
      night: formData.night
    };

    loading = true;
    try {
      const result = isEdit
        ? await timesheetsApi.update(timesheet.id, data)
        : await timesheetsApi.create(data);
      dispatch('success', result);
    } catch (err) {
      // Surface server-side validation (e.g. a 400 for a variant with no
      // configured hourly price) inside the modal itself: this overlay sits
      // on top of the page, so a page-level message banner would render
      // behind it and the failure would look like a silent no-op.
      console.error('[TimesheetModal] Error saving timesheet:', err);
      error = err.message || $_('timesheets.messages.saveError');
    } finally {
      loading = false;
    }
  }

  function handleCancel() {
    dispatch('cancel');
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      handleCancel();
    }
  }

  function getCustomerDisplayName(customer) {
    if (customer.companyName) {
      return customer.companyName;
    }
    if (customer.name || customer.surname) {
      return `${customer.name || ''} ${customer.surname || ''}`.trim();
    }
    return customer.id;
  }

  function getActivityTypeLabel(type) {
    return $_(`timesheets.activityTypes.${type}`);
  }
</script>

<svelte:window on:keydown={handleKeydown} />

<!-- Modal backdrop -->
<div class="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50 fade-in">
  <!-- Modal -->
  <div class="bg-white/95 dark:bg-gray-900/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-200/60 dark:border-gray-700/60 max-w-xl w-full max-h-[90vh] overflow-hidden scale-in flex flex-col">
    <!-- Header -->
    <div class="px-6 py-5 border-b border-gray-200/60 dark:border-gray-700/60 flex items-center justify-between bg-linear-to-r from-primary-50/50 dark:from-primary-900/20 to-transparent">
      <div class="flex items-center space-x-3">
        <div class="w-10 h-10 bg-linear-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
          {#if isEdit}
            <Edit2 size={20} class="text-white" />
          {:else}
            <Clock size={20} class="text-white" />
          {/if}
        </div>
        <div>
          <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">
            {isEdit ? $_('timesheets.edit') : $_('timesheets.add')}
          </h2>
          <p class="text-sm text-gray-500 dark:text-gray-400">
            {$_('timesheets.subtitle')}
          </p>
        </div>
      </div>
      <button
        on:click={handleCancel}
        class="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200"
      >
        <X size={24} />
      </button>
    </div>

    <!-- Content -->
    <div class="p-6 overflow-y-auto flex-1">
      <form on:submit|preventDefault={handleSubmit} class="space-y-5">
        {#if error}
          <div class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-3">
            <div class="text-sm text-red-800 dark:text-red-200">{error}</div>
          </div>
        {/if}

        <!-- Date and Hours row -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label for="date" class="label">
              {$_('timesheets.fields.date')} *
            </label>
            <input
              type="date"
              id="date"
              bind:value={formData.date}
              class="input {errors.date ? 'border-red-500' : ''}"
            />
            {#if errors.date}
              <p class="text-red-500 text-sm mt-1">{errors.date}</p>
            {/if}
          </div>

          <div>
            <label for="hours" class="label">
              {$_('timesheets.fields.hours')} *
            </label>
            <input
              type="number"
              id="hours"
              step="0.25"
              min="0.25"
              max="24"
              bind:value={formData.hours}
              class="input {errors.hours ? 'border-red-500' : ''}"
            />
            {#if errors.hours}
              <p class="text-red-500 text-sm mt-1">{errors.hours}</p>
            {/if}
          </div>
        </div>

        <!-- Customer -->
        <div>
          <label for="customerId" class="label">
            {$_('timesheets.fields.customer')}
          </label>
          <select
            id="customerId"
            bind:value={formData.customerId}
            class="input"
            disabled={loadingCustomers}
          >
            <option value="">{$_('timesheets.filters.allCustomers')}</option>
            {#each customers as customer}
              <option value={customer.id}>{getCustomerDisplayName(customer)}</option>
            {/each}
          </select>
        </div>

        <!-- Activity Type -->
        <div>
          <label for="activityType" class="label">
            {$_('timesheets.fields.activityType')}
          </label>
          <select
            id="activityType"
            bind:value={formData.activityType}
            class="input"
          >
            <option value="">{$_('timesheets.noActivityType')}</option>
            {#each ACTIVITY_TYPES as type}
              <option value={type}>{getActivityTypeLabel(type)}</option>
            {/each}
          </select>
          {#if linkedVariantInfo}
            <p class="text-xs text-primary-600 dark:text-primary-400 mt-1">
              Rate from: {linkedVariantInfo.code} - {linkedVariantInfo.name}
            </p>
          {/if}
        </div>

        <!-- Description -->
        <div>
          <label for="description" class="label">
            {$_('timesheets.fields.description')}
          </label>
          <textarea
            id="description"
            bind:value={formData.description}
            rows="3"
            class="input resize-none"
            placeholder="Descrivi il lavoro svolto..."
          ></textarea>
        </div>

        <!-- Billable, Urgent, Night and Hourly Rate row -->
        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-wrap items-center gap-4 pt-6">
            <div class="flex items-center space-x-2">
              <input
                type="checkbox"
                id="billable"
                bind:checked={formData.billable}
                class="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
              />
              <label for="billable" class="label mb-0 cursor-pointer">
                {$_('timesheets.fields.billable')}
              </label>
            </div>

            <div class="flex items-center space-x-2">
              <input
                type="checkbox"
                id="urgent"
                bind:checked={formData.urgent}
                class="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
              />
              <label for="urgent" class="label mb-0 cursor-pointer">
                {$_('timesheets.fields.urgent')}
              </label>
            </div>

            <div class="flex items-center space-x-2">
              <input
                type="checkbox"
                id="night"
                bind:checked={formData.night}
                class="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
              />
              <label for="night" class="label mb-0 cursor-pointer">
                {$_('timesheets.fields.night')}
              </label>
            </div>
          </div>

          <div>
            <label for="hourlyRate" class="label flex items-center gap-2">
              {$_('timesheets.fields.hourlyRate')} (€)
              {#if lookingUpRate}
                <span class="text-xs text-gray-400 animate-pulse">Ricerca...</span>
              {/if}
            </label>
            <div class="relative">
              <input
                type="number"
                id="hourlyRate"
                step="0.01"
                min="0"
                bind:value={formData.hourlyRate}
                class="input {rateSource ? 'pr-20' : ''}"
                placeholder="0.00"
              />
              {#if rateSource}
                <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs px-2 py-0.5 rounded-full {rateSource === 'customer' ? 'bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-300' : 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'}">
                  {rateSource === 'customer' ? $_('timesheets.rateSource.customer') : $_('timesheets.rateSource.variant')}
                </span>
              {/if}
            </div>
            {#if appliedCondition}
              <p class="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <span>{$_('timesheets.fields.appliedCondition')}:</span>
                <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900 dark:text-fuchsia-200">
                  {$_(`timesheets.conditions.${appliedCondition}`)} · {formatCurrency(formData.hourlyRate)}
                </span>
              </p>
            {/if}
            {#if formData.activityType && !linkedVariantInfo && !lookingUpRate}
              <p class="text-amber-600 dark:text-amber-400 text-xs mt-1 flex items-center gap-1">
                <Info size={12} />
                {$_('timesheets.noVariantLinked')}
              </p>
            {:else if formData.activityType && linkedVariantInfo && notHourly && !lookingUpRate}
              <p class="text-amber-600 dark:text-amber-400 text-xs mt-1 flex items-center gap-1">
                <Info size={12} />
                {$_('timesheets.notHourly')}
              </p>
            {:else if formData.activityType && linkedVariantInfo && noRateConfigured && !lookingUpRate}
              <p class="text-amber-600 dark:text-amber-400 text-xs mt-1 flex items-center gap-1">
                <Info size={12} />
                {$_('timesheets.noRateConfigured')}
              </p>
            {/if}
            <!--
              A non-hourly variant keeps whatever rate is typed here, empty
              included - and an empty rate on a billable entry counts as zero
              in the billing totals. The server cannot refuse that shape (a
              variant-less entry is allowed exactly the same one), so the
              person who left the field empty is told what it will cost.
            -->
            {#if notHourly && formData.billable && !hasHourlyRate && !lookingUpRate}
              <p class="text-amber-600 dark:text-amber-400 text-xs mt-1 flex items-center gap-1">
                <Info size={12} />
                {$_('timesheets.billableWithoutRate')}
              </p>
            {/if}
          </div>
        </div>

        <!-- Calculated total (if billable and rate set) -->
        {#if formData.billable && hasHourlyRate && formData.hours}
          <div class="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-primary-700 dark:text-primary-300">
                {$_('timesheets.fields.totalAmount')}
              </span>
              <span class="text-lg font-bold text-primary-600 dark:text-primary-400">
                € {(parseFloat(formData.hours) * parseFloat(formData.hourlyRate)).toFixed(2)}
              </span>
            </div>
          </div>
        {/if}
      </form>
    </div>

    <!-- Footer -->
    <div class="px-6 py-4 border-t border-gray-200/60 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-800/50 flex justify-end space-x-3">
      <button
        type="button"
        on:click={handleCancel}
        class="btn btn-secondary"
      >
        {$_('common.cancel')}
      </button>
      <button
        type="submit"
        on:click={handleSubmit}
        class="btn btn-primary"
        disabled={loading}
      >
        {#if loading}
          {$_('common.saving')}
        {:else}
          {$_('common.save')}
        {/if}
      </button>
    </div>
  </div>
</div>
